// src/directives/numberOnly.ts
import type { DirectiveBinding, ObjectDirective } from 'vue';

interface NumberOnlyEl extends HTMLInputElement {
  _numberOnlyHandler?: (e: Event) => void;
  _numberOnlyPasteHandler?: (e: ClipboardEvent) => void;
  _numberOnlyKeydownHandler?: (e: KeyboardEvent) => void;
}

export const NumberOnlyDirective: ObjectDirective<NumberOnlyEl> = {
  mounted(el, binding) {
    // 处理键盘输入
    const handleKeydown = (e: KeyboardEvent) => {
      // 阻止小数点、逗号、减号等非数字输入
      if (['.', ',', '-', '+', 'e', 'E'].includes(e.key)) {
        e.preventDefault();
        return;
      }

      // 只允许数字和控制键
      if (
        !/[0-9]/.test(e.key) &&
        ![
          'Backspace',
          'Delete',
          'ArrowLeft',
          'ArrowRight',
          'Tab',
          'Home',
          'End',
        ].includes(e.key)
      ) {
        e.preventDefault();
      }
    };

    // 处理输入变化
    const handleInput = (e: Event) => {
      const target = e.target as HTMLInputElement;
      const { selectionStart, selectionEnd } = target;
      const originalValue = target.value;

      // 过滤非数字字符
      const filteredValue = originalValue.replace(/[^0-9]/g, '');

      if (filteredValue !== originalValue) {
        target.value = filteredValue;

        // 触发v-model更新
        const event = new Event('input', { bubbles: true });
        target.dispatchEvent(event);

        // 恢复光标位置
        const diff = originalValue.length - filteredValue.length;
        const newPosition = Math.max(0, (selectionStart || 0) - diff);
        target.setSelectionRange(newPosition, newPosition);
      }
    };

    // 处理粘贴操作
    const handlePaste = (e: ClipboardEvent) => {
      e.preventDefault();
      const pasteData = e.clipboardData?.getData('text') || '';
      const filteredPaste = pasteData.replace(/[^0-9]/g, '');

      const target = e.target as HTMLInputElement;
      const { selectionStart, selectionEnd } = target;
      const newValue =
        target.value.substring(0, selectionStart || 0) +
        filteredPaste +
        target.value.substring(selectionEnd || 0);

      target.value = newValue.replace(/[^0-9]/g, '');

      const event = new Event('input', { bubbles: true });
      target.dispatchEvent(event);

      const newPosition = (selectionStart || 0) + filteredPaste.length;
      target.setSelectionRange(newPosition, newPosition);
    };

    // 保存处理器引用以便卸载时移除
    el._numberOnlyKeydownHandler = handleKeydown;
    el._numberOnlyHandler = handleInput;
    el._numberOnlyPasteHandler = handlePaste;

    el.addEventListener('keydown', handleKeydown);
    el.addEventListener('input', handleInput);
    el.addEventListener('paste', handlePaste);
  },

  unmounted(el) {
    if (el._numberOnlyKeydownHandler) {
      el.removeEventListener('keydown', el._numberOnlyKeydownHandler);
    }
    if (el._numberOnlyHandler) {
      el.removeEventListener('input', el._numberOnlyHandler);
    }
    if (el._numberOnlyPasteHandler) {
      el.removeEventListener('paste', el._numberOnlyPasteHandler);
    }
  },
};

// 默认导出
export default NumberOnlyDirective;
